/**
 * 鉴权会话 — localStorage + 内存快照 + refresh 单飞
 *
 * 供 api/client 与 useAuth 共用,避免 refresh 循环依赖。
 */
import { AUTH_STORAGE_KEY, SILKREELS_API_BASE, TENANT_HEADER } from './config'
import { getActiveTenantCode } from '@/shared/lib/tenant-code'
import { notifyAuthConfigInvalidate } from '@/shared/lib/auth-config-cache'
import type { SilkAuthResult } from './types'

export interface AuthSnapshot {
  user: SilkAuthResult['user']
  access_token: string
  access_expires_at: string
  refresh_token: string
  refresh_expires_at: string
}

interface ApiEnvelope<T> {
  code: number
  message?: string
  data: T
}

let snapshot: AuthSnapshot | null = null
const listeners = new Set<() => void>()

let refreshInFlight: Promise<boolean> | null = null

export function loadAuthSnapshot(): AuthSnapshot | null {
  if (typeof window === 'undefined') return null
  try {
    const raw = window.localStorage.getItem(AUTH_STORAGE_KEY)
    if (!raw) return null
    const snap = JSON.parse(raw) as AuthSnapshot
    return snap
  } catch {
    return null
  }
}

export function getAuthSnapshot(): AuthSnapshot | null {
  return snapshot ?? loadAuthSnapshot()
}

function persist(next: AuthSnapshot | null) {
  if (typeof window === 'undefined') return
  if (next) {
    window.localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(next))
  } else {
    window.localStorage.removeItem(AUTH_STORAGE_KEY)
  }
}

function notify() {
  for (const l of listeners) l()
}

export function setAuthSnapshot(next: AuthSnapshot | null) {
  snapshot = next
  persist(next)
  notifyAuthConfigInvalidate()
  notify()
  scheduleSessionKeepalive()
}

export function subscribeAuth(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function fromAuthResult(r: SilkAuthResult): AuthSnapshot {
  return {
    user: r.user,
    access_token: r.tokens.access_token,
    access_expires_at: r.tokens.access_expires_at,
    refresh_token: r.tokens.refresh_token,
    refresh_expires_at: r.tokens.refresh_expires_at,
  }
}

export function readAccessToken(): string | null {
  return getAuthSnapshot()?.access_token ?? null
}

const EXPIRY_BUFFER_MS = 60_000

/** 页面打开期间定期续期 access token，避免长时间挂机后首请求才失败 */
const SESSION_KEEPALIVE_MS = 10 * 60_000

let keepaliveTimer: number | null = null

function scheduleSessionKeepalive() {
  if (typeof window === 'undefined') return
  if (keepaliveTimer) {
    window.clearTimeout(keepaliveTimer)
    keepaliveTimer = null
  }
  const snap = getAuthSnapshot()
  if (!snap?.refresh_token) return

  const expires = snap.access_expires_at ? Date.parse(snap.access_expires_at) : NaN
  const delay = Number.isFinite(expires)
    ? Math.max(5_000, expires - Date.now() - EXPIRY_BUFFER_MS)
    : SESSION_KEEPALIVE_MS

  keepaliveTimer = window.setTimeout(() => {
    void ensureFreshAccessToken().finally(() => scheduleSessionKeepalive())
  }, delay)
}

class RefreshTokenError extends Error {
  readonly authRejected: boolean

  constructor(authRejected: boolean, message: string) {
    super(message)
    this.name = 'RefreshTokenError'
    this.authRejected = authRejected
  }
}

function accessTokenExpiringSoon(snap: AuthSnapshot): boolean {
  if (!snap.access_expires_at) return true
  const expires = Date.parse(snap.access_expires_at)
  if (!Number.isFinite(expires)) return true
  return expires - Date.now() <= EXPIRY_BUFFER_MS
}

/** refresh 仍有效且 token 齐全 */
export function hasUsableAuthSession(): boolean {
  const snap = getAuthSnapshot()
  if (!snap?.access_token || !snap.refresh_token) return false
  if (!snap.refresh_expires_at) return true
  const ts = Date.parse(snap.refresh_expires_at)
  if (!Number.isFinite(ts)) return true
  return ts > Date.now()
}

/** 请求前若 access token 即将过期,主动 refresh */
export async function ensureFreshAccessToken(): Promise<void> {
  const snap = getAuthSnapshot()
  if (!snap?.refresh_token) return
  if (!accessTokenExpiringSoon(snap)) return
  await refreshAuthSession()
}

async function refreshTokensRaw(refreshToken: string): Promise<SilkAuthResult> {
  const res = await fetch(`${SILKREELS_API_BASE}/api/v1/auth/refresh`, {
    method: 'POST',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      [TENANT_HEADER]: getActiveTenantCode(),
    },
    credentials: 'include',
    body: JSON.stringify({ refresh_token: refreshToken }),
  })

  const text = await res.text()
  let json: ApiEnvelope<SilkAuthResult> | null = null
  if (text) {
    try {
      json = JSON.parse(text) as ApiEnvelope<SilkAuthResult>
    } catch {
      /* ignore */
    }
  }

  if (!res.ok || !json || json.code !== 0) {
    const authRejected =
      res.status === 401 ||
      (typeof json?.code === 'number' && json.code >= 40100 && json.code <= 40199)
    throw new RefreshTokenError(
      authRejected,
      json?.message ?? res.statusText ?? 'refresh failed',
    )
  }
  return json.data
}

/** refresh token 换新 access;失败则清会话 */
export async function refreshAuthSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight

  refreshInFlight = (async () => {
    const current = getAuthSnapshot()
    if (!current?.refresh_token) {
      setAuthSnapshot(null)
      return false
    }
    try {
      const result = await refreshTokensRaw(current.refresh_token)
      setAuthSnapshot(fromAuthResult(result))
      return true
    } catch (err) {
      if (err instanceof RefreshTokenError && err.authRejected) {
        setAuthSnapshot(null)
      }
      return false
    } finally {
      refreshInFlight = null
    }
  })()

  return refreshInFlight
}

export function redirectToLogin(): void {
  if (typeof window === 'undefined') return
  const { pathname, search } = window.location
  if (pathname.startsWith('/auth/')) return
  const redirect = `${pathname}${search}`
  const target =
    redirect && redirect.startsWith('/') && !redirect.startsWith('//')
      ? `/auth/login?redirect=${encodeURIComponent(redirect)}`
      : '/auth/login'
  window.location.assign(target)
}

export function handleAuthFailure(): void {
  setAuthSnapshot(null)
  redirectToLogin()
}

/** refresh 失败并已触发登录跳转时抛出,UI 层可忽略 */
export class AuthSessionExpiredError extends Error {
  constructor() {
    super('session expired')
    this.name = 'AuthSessionExpiredError'
  }
}

export function isAuthSessionExpiredError(err: unknown): boolean {
  return err instanceof AuthSessionExpiredError
}

if (typeof window !== 'undefined' && !snapshot) {
  snapshot = loadAuthSnapshot()
  if (hasUsableAuthSession()) {
    const bootstrapSession = () => {
      void ensureFreshAccessToken().finally(() => scheduleSessionKeepalive())
    }
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(bootstrapSession, { timeout: 2000 })
    } else {
      globalThis.setTimeout(bootstrapSession, 0)
    }
  }
}
